import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  downloadDeliveryPDF,
  getDropOffProducts,
} from "@/store/slices/newDeliverySlice";

import {
  ArrowLeftIcon,
  MapPinIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { PaperAirplaneIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

/** Figma-aligned tokens */
const GREEN = "#065C36";
const PAGE_BG = "#F9F9F9";
const ACCENT_YELLOW = "#FFB800";

export default function DropoffProduct() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  // IMPORTANT: use delivery.json namespace
  const { t } = useTranslation("delivery");

  const { drop_off_produts, loading } = useSelector((s) => s.delivery);

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(getDropOffProducts({ id }));
    }
  }, [id, dispatch]);

  const handlePrint = () => {
    const list = Array.isArray(drop_off_produts) ? drop_off_produts : [];

    const ids = list
      .map((item) => item.delivery_assigned_order_id)
      .filter(Boolean);

    if (ids.length === 0) {
      Toaster.error(t("no_products_available_to_print"));
      return;
    }

    dispatch(downloadDeliveryPDF(ids)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        const pdfUrl = res.payload?.data || res.payload?.url;

        if (pdfUrl) {
          window.open(pdfUrl, "_blank");
          Toaster.success(t("pdf_generated_successfully"));
        }
      }
    });
  };

  const openMaps = () => {
    const lat = state?.latitude;
    const lng = state?.longitude;
    const address = state?.delivery_landmark || "";

    if (lat != null && lng != null) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
    } else {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`,
        "_blank"
      );
    }
  };

  const handleUploadDeliveryProof = (item) => {

    navigate(`/delivery-partner/delivery-proof-upload/${item.delivery_assigned_order_id || id}`, {
      state: { item },
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: PAGE_BG }}
    >
      {/* HEADER */}
      <header
        className="text-white shrink-0"
        style={{ backgroundColor: GREEN }}
      >
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          <div className="flex flex-col gap-5 md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)_minmax(0,1.1fr)] md:items-start md:gap-x-4 md:gap-y-0">
            <div className="flex items-start justify-between gap-3 md:contents">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-left text-sm text-white hover:opacity-90 min-w-0 md:col-start-1 md:row-start-1 md:pt-1 md:self-start"
              >
                <ArrowLeftIcon className="w-4 h-4 shrink-0" />

                <span className="leading-snug font-medium">
                  <span className="hidden sm:inline">
                    {t("back_to_assigned_order_client_details")}
                  </span>

                  <span className="sm:hidden">
                    {t("back")}
                  </span>
                </span>
              </button>

              <div className="flex justify-end md:col-start-3 md:row-start-1 md:justify-self-end md:pt-1">
                <button
                  type="button"
                  aria-label={t("open_in_google_maps")}
                  className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shadow-lg hover:brightness-110 active:scale-[0.98] transition"
                  style={{ backgroundColor: ACCENT_YELLOW }}
                  onClick={openMaps}
                >
                  <PaperAirplaneIcon className="w-5 h-5 text-white -rotate-45" />
                </button>
              </div>
            </div>

            <div className="text-center md:col-start-2 md:row-start-1 md:-mt-1 md:px-4 md:self-start">
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {t("drop_off_location")} {state?.drop_off_index ?? ""}
              </h1>

              <div className="mt-3 flex justify-center items-start gap-2 text-sm text-white/90 max-w-xl mx-auto">
                <MapPinIcon className="w-4 h-4 shrink-0 mt-0.5 opacity-90" />

                <span className="text-center leading-relaxed break-words">
                  {state?.delivery_landmark || ""}
                </span>
              </div>

              <div className="mt-3 flex justify-center items-start gap-2 text-sm text-white/90 max-w-xl mx-auto">
                <span className="text-center leading-relaxed break-words">
                  {t("address")} {state?.delivery_address || ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto w-full py-8 sm:py-10 px-4 sm:px-6 lg:px-8 flex-1 pb-28">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !drop_off_produts?.length ? (
          <div className="text-center text-gray-500 py-16 rounded-2xl border border-gray-200/80 shadow-sm bg-white">
            {t("no_products_found")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {drop_off_produts.map((item, index) => (
              <article
                key={item.rfq_product_id ?? index}
                className="rounded-2xl p-5 sm:p-6 flex flex-col h-full border border-gray-100/80 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-[88px] h-[88px] sm:w-24 sm:h-24 shrink-0 rounded-xl flex items-center justify-center overflow-hidden">
                    <img
                      src={item.product_image || "/img/photo-gallery.png"}
                      alt={item.product_name || t("product")}
                      className="max-h-full max-w-full object-contain p-1.5"
                    />
                  </div>

                  <div className="min-w-0 flex-1 pt-0.5">
                    <h2 className="font-bold text-[#111827] text-base sm:text-lg leading-snug line-clamp-2">
                      {item.product_name}
                    </h2>

                    {item.sku_number ? (
                      <p className="text-xs font-semibold text-[#111827] mt-2 uppercase tracking-wide">
                        {item.sku_number}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 pt-5 border-gray-100 space-y-4">
                  <div className="flex items-center gap-1">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                      <img
                        src="/img/icons/Containers.png"
                        className="w-7 h-5 object-contain opacity-90"
                        alt=""
                      />
                    </span>

                    <span className="text-sm text-gray-700 font-semibold">
                      {t("type")}: {item.category_name ?? "—"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                      <img
                        src={
                          item.is_installation === true ||
                            item.is_installation === "yes"
                            ? "/img/icons/Container-3.png"
                            : "/img/icons/Margin.png"
                        }
                        className="w-7 h-5 object-contain opacity-90"
                        alt=""
                      />
                    </span>

                    <span className="text-sm text-gray-700 font-semibold">
                      {item.is_installation === true ||
                        item.is_installation === "yes"
                        ? t("delivery_and_installation")
                        : t("delivery")}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                      <img
                        src="/img/icons/self.png"
                        className="w-7 h-5 object-contain opacity-90"
                        alt=""
                      />
                    </span>

                    <span className="text-sm text-gray-700 font-semibold">
                      {t("quantity")}: {item.quantity} {t("units")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                      <img
                        src="/img/icons/self.png"
                        className="w-7 h-5 object-contain opacity-90"
                        alt=""
                      />
                    </span>

                    <span className="text-sm text-gray-700 font-semibold">
                      {t("picked_up_quantity")}: {item.collected_quantity} {t("units")}
                    </span>
                  </div>
                </div>
                {Number(item.collected_quantity) === Number(item.quantity) && (
                  <button
                    type="button"
                    onClick={() => handleUploadDeliveryProof(item)}
                    disabled={item.actual_delivery_proof_uploaded === true}
                    className="w-full bg-[#F4B11F] text-white py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#d99a1a] transition-colors mt-6 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#F4B11F]"
                  >
                    <ArrowUpTrayIcon className="w-5 h-5" />
                    {item.actual_delivery_proof_uploaded === true
                      ? t("delivery_proof_uploaded")
                      : t("upload_delivery_proof")}
                  </button>
                )}
              </article>
            ))}
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer
        className="px-4 sm:px-6 lg:px-8 pt-5 pb-5 sm:pt-6 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))] sticky bottom-0 shadow-[0_-10px_40px_rgba(0,0,0,0.12)]"
        style={{ backgroundColor: GREEN }}
      >
        <div className="max-w-7xl mx-auto flex justify-start">
          <button
            type="button"
            onClick={handlePrint}
            className="w-full max-w-[560px] border border-white text-white py-3.5 rounded-xl flex items-center justify-center gap-2.5 font-medium text-base hover:bg-white/10 active:bg-white/[0.15] transition-colors"
          >
            <PrinterIcon className="w-5 h-5 shrink-0 stroke-[1.75]" />
            {t("print")}
          </button>
        </div>
      </footer>
    </div>
  );
}